import { ChangeDetectionStrategy, Component, Inject, OnInit, signal } from "@angular/core";
import { AbstractControl, FormBuilder, FormGroup, ValidatorFn, Validators } from "@angular/forms";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import { SLOT } from "src/app/common/enums/slot";
import { IProjectEntity } from "src/app/core/interfaces/project-entity";
import { TabConfig } from "src/app/shared/ui/tabs/tabs.component";
import { coordinates, SETTINGS_TABS } from "./constants";

// TODO: remove once the backend stops returning grid-development in moduleComposition (removed from the frontend)
const REMOVED_MODULE_IDS = ["grid-development"];

const getEditableModules = (entity: IProjectEntity): IProjectEntity["moduleComposition"] =>
  entity.moduleComposition.filter(({ id }) => !REMOVED_MODULE_IDS.includes(id));

@Component({
  selector: "app-ca-settings-dialog",
  templateUrl: "./settings.component.html",
  styleUrls: ["./settings.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ConfigAreaSettingsDialogComponent implements OnInit {
  public form: FormGroup;

  readonly tabs: Array<TabConfig> = SETTINGS_TABS;
  readonly tab = signal("municipalSettings");

  constructor(
    @Inject(MAT_DIALOG_DATA)
    public readonly dialogData: {
      entity: IProjectEntity;
    },
    private readonly dialogRef: MatDialogRef<ConfigAreaSettingsDialogComponent>,
    private readonly fb: FormBuilder
  ) {}

  public ngOnInit(): void {
    this.form = this.createForm();
    this.prefillForm(this.form, this.dialogData.entity);
  }

  public closeDialog(submit: boolean = false): void {
    if (!submit) {
      this.dialogRef.close(false);
    } else if (submit && this.form.valid) {
      const data = this.form.getRawValue();
      data.modules.items = data.modules.items.map((module) => {
        let gridConnectionFields = {};
        if (module.id === SLOT.GRID_CONNECTION && module.active) {
          gridConnectionFields = {
            benchmarking: module.benchmarking
          };
        }

        return {
          moduleId: module.id,
          active: module.active,
          config: {
            division: {
              electricity: module.electricity,
              gas: module.gas
            },
            ...gridConnectionFields
          }
        };
      });
      this.dialogRef.close(data);
    }
  }

  private numberRangeValidator(minValue: number, maxValue: number): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
      //check if the input is a number in the format 7/7.0.../12,0...
      if (Validators.pattern(/^\d+(?:[.,]\d+)?$/)(control) !== null) {
        return { patternError: { value: control.value } };
      }
      let value = control.value;
      if (typeof value === "string") {
        value = value.replace(",", ".");
        value = Number(value);
      }
      if (value < minValue || value > maxValue) {
        return { rangeError: { value: control.value } };
      }
      return null;
    };
  }

  private createForm(): FormGroup {
    const municipality = this.fb.group({
      name: ["", Validators.required],
      entityId: [{ value: "", disabled: true }],
      ags: [{ value: "", disabled: true }],
      coordinatesLong: ["", this.numberRangeValidator(coordinates.longitude.min, coordinates.longitude.max)],
      coordinatesLat: ["", this.numberRangeValidator(coordinates.latitude.min, coordinates.latitude.max)],
      zoomLevel: [null, Validators.required],
      coatOfArms: [""],
      newsNotification: [null]
    });
    const contactPerson = this.fb.group({
      role: [{ value: "", disabled: true }, Validators.required],
      salutation: [{ value: "", disabled: true }, Validators.required],
      givenName: ["", Validators.required],
      familyName: ["", Validators.required],
      email: ["", [Validators.required, Validators.email]],
      address: ["", Validators.required],
      phoneNumber: ["", Validators.required]
    });
    const commonFields = {
      id: [{ value: "", disabled: true }],
      active: [{ value: false, disabled: false }],
      electricity: [{ value: false, disabled: false }],
      gas: [{ value: false, disabled: false }]
    };
    const gridConnectionFields = {
      benchmarking: [{ value: false, disabled: false }]
    };
    const modules = this.fb.group({
      concession: [{ value: false, disabled: false }],
      electricity: [{ value: false, disabled: false }],
      gas: [{ value: false, disabled: false }],
      items: this.fb.array(
        getEditableModules(this.dialogData.entity).map((module) => {
          let fields = { ...commonFields };

          if (module.id === SLOT.GRID_CONNECTION) {
            fields = { ...fields, ...gridConnectionFields };
          }

          return this.fb.group(fields);
        })
      )
    });

    return this.fb.group({ municipality, contactPerson, modules });
  }

  private prefillForm(form: FormGroup, data: IProjectEntity): void {
    form.patchValue({
      contactPerson: data.contactPerson,
      municipality: {
        entityId: data.entityId,
        ags: data.entity.ags,
        name: data.entity.name,
        coordinatesLong: `${data.entity.position.longitude}`,
        coordinatesLat: `${data.entity.position.latitude}`,
        zoomLevel: data.entity.level,
        coatOfArms: data.entity.coatOfArms,
        newsNotification: data.features?.newsNotification
      },
      modules: {
        concession: data.config.concession,
        electricity: data.config.division.electricity,
        gas: data.config.division.gas,
        items: getEditableModules(data).map((module) => {
          let fields: any = {
            id: module.id,
            active: module.active,
            electricity: module.config.division.electricity,
            gas: module.config.division.gas
          };

          if (module.id === SLOT.GRID_CONNECTION) {
            fields = {
              ...fields,
              benchmarking: module.config.benchmarking
            };
          }

          return fields;
        })
      }
    });
  }
}
